/**
 * @file Renderable component: the bridge between an entity and its mesh.
 *
 * This is the ONLY component that holds a Three.js object. Gameplay systems
 * write to {@link ../components/Transform.js Transform}; the render system is
 * what copies transforms onto meshes, so game rules never touch the scene graph
 * (GDD §25).
 */

import { Component } from '../ecs/Component.js';

export class Renderable extends Component {
  /**
   * @param {string} prefabKey Key the mesh was acquired with, needed to pool it back.
   * @param {import('three').Object3D} object Mesh or group representing the entity.
   * @param {object} [options] Extra options.
   * @param {number} [options.yOffset] Height added to the transform position.
   * @param {number} [options.proneYOffset] Height used while the body is lying down.
   * @param {boolean} [options.faceVelocity] Orient from velocity instead of `rotationY`.
   */
  constructor(prefabKey, object, { yOffset = 0, proneYOffset = yOffset, faceVelocity = false } = {}) {
    super();
    /** @type {string} */
    this.prefabKey = prefabKey;
    /** @type {import('three').Object3D} */
    this.object = object;
    /** @type {number} */
    this.yOffset = yOffset;
    /** @type {number} */
    this.proneYOffset = proneYOffset;
    /** @type {boolean} */
    this.faceVelocity = faceVelocity;
    /** @type {boolean} Set by the render system once the object is in the scene. */
    this.attached = false;
    /** @type {boolean} Skips the transform copy, e.g. for static geometry. */
    this.isStatic = false;
  }

  /** @returns {void} */
  reset() {
    this.attached = false;
  }
}
